import * as _ from 'lodash';
import { CreateOptions, ESRecordResolver } from '../PA.Core/lib/ESRecordResolver';
import { PAdbContext } from '../PA.Core/PAdbContext';
import {esWB_Company, esWB_SalesArea, esWB_SalesOrg} from '../PA.Core/PAdbSchema';
import {WB_CompanyRow, WB_EntityRow, WB_SalesAreaRow, WB_SalesOrgRow} from '../PA.Core/PAdbSchemaRows';
import { PASearch } from '../PA.Core/PASearch';
import {
    IEntity,
    IEntityCreateOptions,
    IEntityHeader,
    IEntityManager,
    IEntityManagerFindOptions
} from "../PA.MD.Core/PA.MD.IF.Entity";
import { CompanyEntity } from './CompanyEntity';
import { EntityTypeUtils } from './EntityTypeUtils';
import { SalesAreaEntity } from './SalesAreaEntity';
import { SalesOrgEntity } from './SalesOrgEntity';

const vwbEntity_Entity_Search_Fields: {[id: string] : {type: string}} = {};
let stringFields = ['ID_UI', 'KEY_SET', 'NAME', 'MDSET_ID', 'MDSET_NAME', 'ENTITY_ID', 'ENTITY_NAME', 'SALESORG_ID',
    'SALESORG_NAME', 'SALESAREA_ID', 'SALESAREA_NAME', 'COMPANY_ID', 'COMPANY_NAME']
let numberFields = ['WB_ID', 'WB_MDSET_ID', 'WB_ENTITY_ID', 'WB_SALESORG_ID', 'WB_SALESAREA_ID', 'WB_COMPANY_ID']
_.forEach(stringFields, (a) => {
    vwbEntity_Entity_Search_Fields[a] = {type: "string"};
})
_.forEach(numberFields, (a) => {
    vwbEntity_Entity_Search_Fields[a] = {type: "number"}
})

export class EnterpriseEntityManager
    implements
        IEntityManager
{

    async findEntityByType(wbEntityId: number, wbId: number, options?: IEntityManagerFindOptions): Promise<IEntity> {
        let arrEntities = await this.findEntitiesByType(wbEntityId, [wbId], options)
        return arrEntities.length > 0 ? arrEntities[0] : undefined;
    }
    async findEntitiesByType(wbEntityId: number, arrWbId: number[], options?: IEntityManagerFindOptions): Promise<IEntity[]> {
        let dbContext: PAdbContext = options?.dbContext
        if (_.isEmpty(dbContext))
            dbContext = new PAdbContext();
        let et: WB_EntityRow = await EntityTypeUtils.getEntityTypeRow(wbEntityId)
        if (_.isEmpty(et))
            return []

        switch (et.cif.META_TYPE) {
            case 'SalesArea':
                return await this.getSalesAreaEntities(arrWbId, dbContext);
            case 'SalesOrg':
                return await this.getSalesOrgEntities(arrWbId, dbContext)
            case 'Company':
                return await this.getCompanyEntities(arrWbId, dbContext)
        }
    }


    private async getSalesAreaEntities(arrWbId: number[], dbContext: PAdbContext) : Promise<IEntity[]> {
        let rv: IEntity[] = [];
        const salesarea_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
            dctExcludeHasManyNamedRelations: {}
        }
        
        let rr = new ESRecordResolver(dbContext, salesarea_options)
        let rows = <WB_SalesAreaRow[]> await rr.findAllWithRelationships(esWB_SalesArea, arrWbId, {})
        for (let aRow of rows) {
            let ce = new SalesAreaEntity(aRow)
            await ce.OnLoadComplete()
            rv.push(ce)
        }
        return rv;
    }

    private async getSalesOrgEntities(arrWbId: number[], dbContext: PAdbContext): Promise<IEntity[]> {
        let rv: IEntity[] = [];
        const options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
            dctExcludeHasManyNamedRelations: {}
        }
        
        let rr = new ESRecordResolver(dbContext, options)
        let rows = <WB_SalesOrgRow[]> await rr.findAllWithRelationships(esWB_SalesOrg, arrWbId, {})
        for (let aRow of rows) {
            let ce = new SalesOrgEntity(aRow)
            await ce.OnLoadComplete()
            rv.push(ce)
        }
        return rv;
    
    }

    private async getCompanyEntities(arrWbId: number[], dbContext: PAdbContext) : Promise<IEntity[]> {
        let rv: IEntity[] = [];
        const company_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
            dctExcludeHasManyNamedRelations: {}
        }
        
        let rr = new ESRecordResolver(dbContext, company_options)
        let rows = <WB_CompanyRow[]> await rr.findAllWithRelationships(esWB_Company, arrWbId, {})
        for (let aRow of rows) {
            let ce = new CompanyEntity(aRow)
            await ce.OnLoadComplete()
            rv.push(ce)
        }
        return rv;
    }

    async findEntityHeader(wbEntityId: number, wbId: number, options?: IEntityManagerFindOptions): Promise<IEntityHeader> {

        let dbContext: PAdbContext = options?.dbContext
        if (_.isEmpty(dbContext))
            dbContext = new PAdbContext()
        let et = await EntityTypeUtils.getEntityTypeRow(wbEntityId)
        if (_.isEmpty(et))
            return
        if(!(wbId > 0))
            return undefined


        switch (et.cif.META_TYPE) {
            case 'SalesArea':
                let arrSARow = <WB_SalesAreaRow[]> await dbContext.findUsingCache(esWB_SalesArea, [wbId])
                if(_.isEmpty(arrSARow))
                    return undefined

                let saRow = _.first(arrSARow)
                return {
                    dbContext: dbContext,
                    wbMasterDataSetId: et.cif.WB_MDSET_ID,
                    mapperName: esWB_SalesArea,
                    wb_id: wbId,
                    id_ui: saRow.cif.SALESAREA_ID,
                    key_set: saRow.cif.ENTERPRISE_SET_ID,
                    name: saRow.cif.SALESAREA_NAME
                }
            case 'SalesOrg':
                let arrSORow = <WB_SalesOrgRow[]> await dbContext.findUsingCache(esWB_SalesOrg, [wbId])
                if(_.isEmpty(arrSORow))
                    return undefined

                let soRow = _.first(arrSORow)
                return {
                    dbContext: dbContext,
                    wbMasterDataSetId: et.cif.WB_MDSET_ID,
                    mapperName: esWB_SalesOrg,
                    wb_id: wbId,
                    id_ui: soRow.cif.SALESORG_ID,
                    key_set: soRow.cif.ENTERPRISE_SET_ID,
                    name: soRow.cif.SALESORG_NAME
                }
            case 'Company':
                let arrRow = <WB_CompanyRow[]> await dbContext.findUsingCache(esWB_Company, [wbId])
                if(_.isEmpty(arrRow))
                    return undefined

                let row = _.first(arrRow)
                return {
                    dbContext: dbContext,
                    wbMasterDataSetId: et.cif.WB_MDSET_ID,
                    mapperName: esWB_Company,
                    wb_id: wbId,
                    id_ui: row.cif.COMPANY_ID,
                    key_set: row.cif.ENTERPRISE_SET_ID,
                    name: row.cif.COMPANY_NAME
                }
            default:
                return undefined
        }

    }



    async queryEntitiesByType(wbEntityId: number, condition: { [id: string]: any; }): Promise<number[]> {
        let cond = condition || {};
        if ('where' in cond)
            cond = condition.where || {}

        let pCond = {where: {}}
        if (!_.isEmpty(cond))
            pCond = {where: cond}

        // @ts-ignore
        pCond.where.WB_ENTITY_ID = wbEntityId

        let results = await PASearch.find('vUI_SalesArea_Entity_Search',
            pCond,
            vwbEntity_Entity_Search_Fields,
            [],
            0,
            ['WB_ID'])

        return _.map(results, (a) => +a.WB_ID)
    }
    
    async queryEntitiesByTypeEnterpriseType(wbEntityId: number, wbEntityId_Enterprise: number, condition: { [id: string]: any; }): Promise<{ wbId: number; wbId_Ent: number; }[]> {
        let cond = condition || {}
        if ('where' in condition)
            cond = condition.where || {}

        let pCond = {where: {}}
        if (!_.isEmpty(cond))
            pCond = {where: cond}

        // @ts-ignore
        pCond.where.WB_ENTITY_ID = wbEntityId

        let sEntWbIdFieldName = ''
        let wbEntityRow_Ent: WB_EntityRow = await EntityTypeUtils.getEntityTypeRow(wbEntityId_Enterprise)
        if(_.isEmpty(wbEntityRow_Ent))
            return []

        switch (wbEntityRow_Ent.cif.ENTITY_ID) {
            case "SalesOrg":
                sEntWbIdFieldName = "WB_SALESORG_ID"
            case "SalesArea":
                sEntWbIdFieldName = "WB_SALESAREA_ID"
            case "Company":
                sEntWbIdFieldName = "WB_COMPANY_ID"
        }

        if(_.isEmpty(sEntWbIdFieldName))
            return []


        let results = await PASearch.find('vUI_SalesArea_Entity_Search',
            pCond,
            vwbEntity_Entity_Search_Fields,
            [],
            0,
            ['WB_ID', sEntWbIdFieldName])

        return _.map(results, (a) => { return  {wbId:  +a.WB_ID, wbId_Ent: +a[sEntWbIdFieldName]}})

    }


    async checkEntityExists(wbEntityId: number, key_set: string, id_ui: string): Promise<{ wbId: number; wbEntityIds: number[]; entityIds: string[]; dupError?: boolean; }> {
        let e: WB_EntityRow = await EntityTypeUtils.getEntityTypeRow(wbEntityId);
        
        let results: any[] = await PASearch.find('vUI_Enterprise_Search',
            {
                where: {
                    WB_MDSET_ID: e.cif.WB_MDSET_ID,
                    ID_UI: id_ui,
                    KEY_SET: key_set
                }
            },
            vwbEntity_Entity_Search_Fields,
            [],
            0,
            ['WB_ID', 'WB_ENTITY_ID', 'ENTITY_ID'])

        if (results.length) {
            let wbId = +results[0].WB_ID
            let rv: {wbId: number, wbEntityIds: number[], entityIds: string[], dupError?: boolean} = {
                wbId: wbId,
                wbEntityIds: [],
                entityIds: []
            }

            _.forEach(results, (aRow) => {
                let rWbId = +aRow.WB_ID
                let rWbEntityId = +aRow.WB_ENTITY_ID
                let rEntityId = aRow.ENTITY_ID
                rv.wbEntityIds.push(rWbEntityId)
                rv.entityIds.push(rEntityId)

                if (rWbId != rv.wbId)
                    rv.dupError = true
            })

            return rv
        } else {
            return {wbId: undefined, wbEntityIds: [], entityIds: []}
        }
    }
    
    async createEntity(_wbEntityId: number, key_set: string, id_ui: string, name: string, options?: IEntityCreateOptions): Promise<IEntity> {
        throw new Error("Cannot create Enterprise entities")
    }

}
